import React, { FC, memo, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import styles from './PromotionBar.module.scss';
import Image from 'next/image';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useLanguage } from '@/hooks/use-language';
import { useMobile } from '@/hooks/use-mobile';

dayjs.extend(duration);

type Props = {
  barData: any;
};

const PromotionBar: FC<Props> = memo(props => {
  const pageData = props.barData;
  const barData = pageData?.type_of_promotion?.[0] || null;
  const stickyBar = barData?.sticky_bar;
  const stickyBarUrl = stickyBar?.sticky_bar_url;
  const openInNewTab = stickyBar?.open_in_new_tab;
  const [isValidCountDown, setIsValidCountDown] = useState(false);
  const isMobile = useMobile();
  const [day, setDay] = useState<number | string>('00');
  const [hour, setHour] = useState('00');
  const [minutes, setMinutes] = useState('00');
  const [seconds, setSeconds] = useState('00');
  const language = useLanguage();

  const getTime = () => {
    const formatTime = (time: number) => {
      let timeStr = time.toString();
      if (timeStr.length === 1) {
        return `0${timeStr}`;
      } else {
        return timeStr;
      }
    };
    const endTime = dayjs(pageData?.end_date);
    const currentTime = dayjs();
    const duration = dayjs.duration(endTime.diff(currentTime));
    const day =
      dayjs(endTime.format('YYYY-MM-DD')).diff(dayjs().format('YYYY-MM-DD'), 'day') || '00';
    const hour = formatTime(duration.hours());
    const minutes = formatTime(duration.minutes());
    const seconds = formatTime(duration.seconds());

    return {
      day,
      hour,
      minutes,
      seconds
    };
  };

  const setValidCountDown = () => {
    const startDateData = pageData?.start_date || '';
    const endDateData = pageData?.end_date || '';
    const startDate: any = startDateData ? new Date(startDateData) : null;
    const endDate: any = endDateData ? new Date(endDateData) : null;
    const currentDate = new Date();
    if (startDateData && !endDateData) {
      setIsValidCountDown(currentDate >= startDate);
    } else if (!startDateData && endDateData) {
      setIsValidCountDown(currentDate <= endDate);
    } else if (startDateData && endDateData) {
      setIsValidCountDown(currentDate >= startDate && currentDate <= endDate);
    } else if (!startDateData && !endDateData) {
      setIsValidCountDown(true);
    }
  };

  const handleBtnClick = (event: any, href: string) => {
    window?.dataLayer?.push({
      event: 'buttonClick',
      buttonTitle: 'blog_banner_sticky',
      description: stickyBar?.sticky_bar_text,
      destinationUrl: href,
      pageURL: window.location.href,
      componentName: 'sticky button'
    });
    event.preventDefault();
    if (href) {
      window.open(href, openInNewTab);
    }
  };

  useEffect(() => {
    setValidCountDown();
  }, [seconds]);

  useEffect(() => {
    const countDown = () => {
      const { day, hour, minutes, seconds } = getTime();
      setDay(day);
      setHour(hour);
      setMinutes(minutes);
      setSeconds(seconds);
    };
    countDown();
    const timer = setInterval(countDown, 1000);
    return () => {
      clearInterval(timer);
    };
  }, []);

  const countDown = useMemo(() => {
    const { day, hour, minutes, seconds } = getTime();
    const isInvalidTime = day == '00' && hour == '00' && minutes == '00' && seconds == '00';

    return isInvalidTime ? (
      <></>
    ) : Number(day) < 7 ? (
      <div className={styles.count_down_block}>
        <Image
          className={styles.clock}
          src={isMobile ? '/blog/icon/ClockBlack.svg' : '/blog/icon/ClockWhite.svg'}
          alt={''}
          width={16}
          height={16}
        />
        <span className={styles.count_down_label}>
          {language === 'zh' ? '限時倒數' : 'Ends in'}
        </span>
        <div className={styles.count_down_item}>
          <div className={styles.count}>{day}</div>
          <span className={styles.count_name}>{language === 'zh' ? '日' : 'd'}</span>
        </div>
        <div className={styles.count_down_item}>
          <div className={styles.count}>{hour}</div>
          <span className={styles.count_name}>{language === 'zh' ? '小時' : 'h'}</span>
        </div>

        <div className={styles.count_down_item}>
          <div className={styles.count}>{minutes}</div>
          <span className={styles.count_name}>{language === 'zh' ? '分' : 'm'}</span>
        </div>
        <div className={styles.count_down_item}>
          <div className={styles.count}>{seconds}</div>
          <span className={styles.count_name}>{language === 'zh' ? '秒' : 's'}</span>
        </div>
      </div>
    ) : (
      <div className={styles.count_down_block}>
        <Image
          className={styles.clock}
          style={{ display: 'block' }}
          src={isMobile ? '/blog/icon/ClockBlack.svg' : '/blog/icon/ClockWhite.svg'}
          alt={''}
          width={16}
          height={16}
        />
        <span className={styles.count_down_label}>
          {language === 'zh'
            ? `優惠至 ${dayjs(dayjs(pageData?.end_date)).format('YYYY年MM月DD日')}`
            : `Offer ends on ${dayjs(dayjs(pageData?.end_date)).format('DD MMM YYYY')}`}
        </span>
      </div>
    );
  }, [isMobile, day, hour, minutes, seconds]);

  return (
    <>
      {barData && stickyBar?.sticky_bar_text && stickyBar?.sticky_bar_button ? (
        <div
          className={classNames(styles.promotion_bar, isValidCountDown && styles.valid_count_down)}
        >
          <div className={styles.left_wrapper} id="left_wrapper">
            <p>{stickyBar?.sticky_bar_text}</p>
            {isMobile && isValidCountDown && countDown}
          </div>
          <div className={styles.rectangle_block}>
            {isMobile && isValidCountDown && (
              <Image
                className={styles.big_rectangle}
                src="/blog/icon/FWDRectangleLarge.svg"
                alt={''}
                width={28}
                height={87}
              />
            )}
            {isMobile && !isValidCountDown && (
              <Image
                className={styles.middle_rectangle}
                src="/blog/icon/FWDRectangleMiddle.svg"
                alt={''}
                width={26}
                height={60}
              />
            )}
            {!isMobile && (
              <Image
                className={styles.small_rectangle}
                src="/blog/icon/FWDRectangleSmall.svg"
                alt={''}
                width={31}
                height={56}
              />
            )}
          </div>
          <div
            className={classNames(
              styles.right_wrapper,
              isValidCountDown && styles.valid_count_down
            )}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {!isMobile && isValidCountDown && countDown}
              <a
                href={stickyBarUrl}
                onClick={e => handleBtnClick(e, stickyBarUrl)}
                target={openInNewTab ? '_blank' : ''}
              >
                <button className={styles.Button}>{stickyBar?.sticky_bar_button}</button>
              </a>
            </div>
          </div>
        </div>
      ) : (
        ''
      )}
    </>
  );
});

export default PromotionBar;
